import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { ArrowLeft, ExternalLink, ShoppingBag, Palette } from "lucide-react";
import PreOrderNotify from "@/components/PreOrderNotify";
import BookShareSocials from "@/components/BookShareSocials";
import PumpkinButton from "@/components/PumpkinButton";
import BearButton from "@/components/BearButton";
import SunButton from "@/components/SunButton";
import FavoriteButton from "@/components/FavoriteButton";
import BookComments from "@/components/BookComments";

export default function BookDetail() {
  const { id } = useParams();
  const [book, setBook] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/books/${id}`).then(({ data }) => setBook(data)).catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="book-not-found">
        <h1 className="font-fredoka text-4xl" style={{ color: "#2D3055" }}>This story is hiding.</h1>
        <p className="mt-3" style={{ color: "#4A4E6D" }}>We couldn't find that book. Try wandering back to the library.</p>
        <Link to="/books" className="btn-terracotta mt-6 inline-flex" data-testid="book-back-link">
          <ArrowLeft size={16} /> Back to the Books
        </Link>
      </div>
    );
  }

  if (!book) {
    return <div className="max-w-3xl mx-auto px-6 py-24 font-nunito" style={{ color: "#7E82A1" }}>Turning the page…</div>;
  }

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-14" data-testid="book-detail-page">
      <Link to="/books" className="inline-flex items-center gap-1 font-fredoka text-sm mb-8" style={{ color: "#6EC4E8" }} data-testid="book-back-link">
        <ArrowLeft size={16} /> Back to the library
      </Link>

      <div className="grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div
            className="rounded-3xl overflow-hidden shadow-2xl aspect-[3/4] flex items-center justify-center"
            style={{
              boxShadow: "0 30px 80px rgba(224,122,95,0.2)",
              background: "linear-gradient(160deg,#FFF3F8 0%,#FFFFFF 60%,#F0EBFF 100%)",
            }}
          >
            <img src={book.cover_image_url} alt={book.title} className="w-full h-full object-contain" />
          </div>
          {book.publish_date && (
            <p className="mt-4 text-sm font-caveat text-xl" style={{ color: "#6EC4E8" }}>
              Published {book.publish_date}
            </p>
          )}
        </div>

        <div className="lg:col-span-7">
          {book.age_range && <p className="font-caveat text-2xl" style={{ color: "#FF6B9D" }}>{book.age_range}</p>}
          <h1 className="font-fredoka text-5xl sm:text-6xl mt-1" style={{ color: "#2D3055" }} data-testid="book-title">
            {book.title}
          </h1>
          {book.subtitle && (
            <p className="mt-3 font-caveat text-3xl" style={{ color: "#6EC4E8" }}>{book.subtitle}</p>
          )}

          <div className="mt-8 prose-warm font-nunito text-lg">
            <p>{book.description}</p>
          </div>

          {book.excerpt && (
            <div className="mt-8 paper-card p-6">
              <p className="font-caveat text-3xl leading-tight" style={{ color: "#2D3055" }}>
                "{book.excerpt}"
              </p>
              <p className="mt-3 text-sm font-nunito" style={{ color: "#7E82A1" }}>— an excerpt</p>
            </div>
          )}

          <div className="mt-10">
            <h3 className="font-fredoka text-2xl mb-4" style={{ color: "#2D3055" }}>Where to find it</h3>
            {(book.amazon_url || book.barnes_noble_url || book.direct_url || book.other_url) ? (
              <div className="flex flex-wrap gap-3">
                {book.amazon_url && (() => {
                  const isPumpkin = /pumpkin/i.test(book.title);
                  const isLight = /the world needs your light/i.test(book.title);
                  const isSun = /sun,?\s*shine/i.test(book.title);
                  if (isPumpkin) {
                    return <PumpkinButton href={book.amazon_url} label="Amazon" />;
                  }
                  if (isLight) {
                    return <BearButton href={book.amazon_url} label="Amazon" />;
                  }
                  if (isSun) {
                    return <SunButton href={book.amazon_url} label="Amazon" />;
                  }
                  return (
                    <a
                      href={book.amazon_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-terracotta"
                      data-testid="book-buy-amazon"
                    >
                      <ShoppingBag size={16} /> Amazon <ExternalLink size={14} />
                    </a>
                  );
                })()}
                {book.barnes_noble_url && (
                  <a href={book.barnes_noble_url} target="_blank" rel="noopener noreferrer" className="btn-moss" data-testid="book-buy-bn">
                    <ShoppingBag size={16} /> Barnes & Noble <ExternalLink size={14} />
                  </a>
                )}
                {book.direct_url && (
                  <a href={book.direct_url} target="_blank" rel="noopener noreferrer" className="btn-ghost-warm" data-testid="book-buy-direct">
                    Buy Direct <ExternalLink size={14} />
                  </a>
                )}
                {book.other_url && (
                  <a href={book.other_url} target="_blank" rel="noopener noreferrer" className="btn-ghost-warm" data-testid="book-buy-other">
                    Other Retailer <ExternalLink size={14} />
                  </a>
                )}
                <FavoriteButton bookId={book.id} variant="pill" />
              </div>
            ) : (
              <div className="space-y-4">
                <PreOrderNotify publishDate={book.publish_date} bookTitle={book.title} />
                <FavoriteButton bookId={book.id} variant="pill" />
              </div>
            )}
          </div>

          <div className="mt-8">
            <Link
              to={`/coloring/${book.id}`}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full font-fredoka text-sm transition-transform hover:-translate-y-0.5"
              style={{
                background: "linear-gradient(135deg,#FFD93D 0%,#FF8C42 100%)",
                color: "#2D3055",
                boxShadow: "0 10px 22px rgba(255,140,66,0.30)",
              }}
              data-testid="book-coloring-link"
            >
              <Palette size={16} /> Free printable coloring page
            </Link>
          </div>

          {book.share_image_url && <BookShareSocials book={book} />}

          <BookComments bookId={book.id} />
        </div>
      </div>
    </div>
  );
}
